import { useState, useMemo } from "react";
import { TableProps } from "@cloudscape-design/components";

interface UseTableSortingOptions<T> {
  items: T[];
  defaultSortingField?: string;
  defaultSortingDescending?: boolean;
  sortingComparator: (
    a: T,
    b: T,
    sortingField: string
  ) => number;
}

interface UseTableSortingResult<T> {
  sortedItems: T[];
  sortingColumn: TableProps.SortingColumn<T>;
  sortingDescending: boolean;
  setSortingColumn: (column: TableProps.SortingColumn<T>) => void;
  setSortingDescending: (descending: boolean) => void;
  handleSortingChange: (event: { detail: { sortingColumn: TableProps.SortingColumn<T>; isDescending?: boolean } }) => void;
}

/**
 * Generic hook for table sorting functionality
 * Handles sorting state and logic for Cloudscape Table components
 */
export function useTableSorting<T>({
  items,
  defaultSortingField,
  defaultSortingDescending = true,
  sortingComparator,
}: UseTableSortingOptions<T>): UseTableSortingResult<T> {
  const [sortingColumn, setSortingColumn] = useState<TableProps.SortingColumn<T>>({
    sortingField: defaultSortingField,
  });
  const [sortingDescending, setSortingDescending] = useState(defaultSortingDescending);

  const sortedItems = useMemo(() => {
    const sorted = [...items];
    if (sortingColumn.sortingField) {
      sorted.sort((a, b) => {
        const comparison = sortingComparator(a, b, sortingColumn.sortingField!);
        return sortingDescending ? -comparison : comparison;
      });
    }
    return sorted;
  }, [items, sortingColumn, sortingDescending, sortingComparator]);

  const handleSortingChange = (event: {
    detail: { sortingColumn: TableProps.SortingColumn<T>; isDescending?: boolean };
  }) => {
    setSortingColumn(event.detail.sortingColumn);
    setSortingDescending(event.detail.isDescending ?? false);
  };

  return {
    sortedItems,
    sortingColumn,
    sortingDescending,
    setSortingColumn,
    setSortingDescending,
    handleSortingChange,
  };
}

/**
 * Helper function to create a standard comparator for common field types
 */
export function createStandardComparator<T>(
  fieldExtractors: Record<string, (item: T) => string | number>
): (a: T, b: T, sortingField: string) => number {
  return (a: T, b: T, sortingField: string) => {
    const extractor = fieldExtractors[sortingField];
    if (!extractor) {
      return 0;
    }

    const aValue = extractor(a);
    const bValue = extractor(b);

    if (aValue < bValue) return -1;
    if (aValue > bValue) return 1;
    return 0;
  };
}
